import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { FolderKanban, Plus, Search, Clock, Users, DollarSign, ArrowRight, TrendingUp, Loader2 } from 'lucide-react';
import { demoProjects } from '../data/dummyData';
import { AinaaBadge } from '../components/AinaaBadge';
import { authHeaders } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

export default function ProjectsPage() {
  const [projects, setProjects] = useState(demoProjects);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', project_type: 'Feature Film', description: '', budget: '', tags: '' });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/projects`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (data.length > 0) setProjects(data); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount, API is module constant

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...form, budget: Number(form.budget) || 0, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) }),
      });
      if (res.ok) {
        const p = await res.json();
        setProjects(prev => [p, ...prev]);
        setForm({ name: '', project_type: 'Feature Film', description: '', budget: '', tags: '' });
        setDialogOpen(false);
      }
    } catch { /* handled silently */ } finally { setCreating(false); }
  };

  const filtered = projects.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.project_type?.toLowerCase().includes(search.toLowerCase()) || (p.ainaa_id && p.ainaa_id.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="projects-page">
      <motion.div variants={fadeUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">{projects.length} projects across all stages.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2 h-9" data-testid="create-project-btn"><Plus className="w-4 h-4" strokeWidth={2} /> New Project</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New Project</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Project name" className="mt-1.5" data-testid="project-name-input" /></div>
              <div><Label className="text-xs">Type</Label>
                <Select value={form.project_type} onValueChange={v => setForm({...form, project_type: v})}>
                  <SelectTrigger className="mt-1.5" data-testid="project-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Feature Film', 'Ad Campaign', 'Music Video', 'Product Design', 'Brand Launch', 'Documentary', 'Wedding Film', 'Web Series'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Brief description..." className="mt-1.5" rows={3} data-testid="project-desc-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">Budget</Label><Input type="number" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} placeholder="0" className="mt-1.5" data-testid="project-budget-input" /></div>
                <div><Label className="text-xs">Tags</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="tag1, tag2" className="mt-1.5" data-testid="project-tags-input" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCreate} disabled={creating || !form.name.trim()} className="gap-2" data-testid="submit-project-btn">
                {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Create Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Filters */}
      <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-projects-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-9" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="planning">Planning</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on-hold">On Hold</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Project Cards */}
      <motion.div variants={fadeUp} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(p => (
          <Link to={`/projects/${p.project_id}`} key={p.project_id} className="block" data-testid={`project-card-${p.project_id}`}>
            <div className="p-5 rounded-xl border border-border/50 bg-card hover:border-border transition-all duration-200 h-full group">
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium truncate group-hover:text-foreground">{p.name}</h3>
                  <Badge variant="secondary" className="text-[10px] h-5 px-1.5 mt-1">{p.project_type}</Badge>
                </div>
                <Badge variant="outline" className={`text-[10px] h-5 px-1.5 capitalize flex-shrink-0 border-0 ${
                  p.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' :
                  p.status === 'on-hold' ? 'bg-amber-500/10 text-amber-600' :
                  p.status === 'completed' ? 'bg-zinc-500/10 text-zinc-500' :
                  'bg-secondary text-muted-foreground'
                }`}>{p.status}</Badge>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 mb-4">{p.description}</p>
              <div className="w-full h-1.5 rounded-full bg-secondary mb-3">
                <div className="h-full rounded-full bg-[hsl(36,80%,52%)] transition-all" style={{ width: `${p.progress || 0}%` }} />
              </div>
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" strokeWidth={2} />{p.team_size}</span>
                <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" strokeWidth={2} />{p.budget ? `₹${(p.budget/100000).toFixed(1)}L` : '—'}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={2} />{p.progress || 0}%</span>
              </div>
            </div>
          </Link>
        ))}
      </motion.div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <FolderKanban className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" strokeWidth={2} />
          <p className="text-sm text-muted-foreground">No projects found</p>
        </div>
      )}
    </motion.div>
  );
}
